<template>
  <v-card id="tract-polygon-editor" :class="containerClass" flat>
    <v-card flat :id="mapContainerId" style="overflow: hidden;">
      <MapOverlays :map="map" autoShowSpecies :suppressTips="toolActive" ref="mapOverlays" @state="overlayState = $event" @toggle-species="toggleSpeciesLayer"/>
      <v-card outlined :style="instructionsStyle" :class="instructionsClass">
        <v-card-text>
          <v-row v-for="(i, index) in instructions" :key="'instr-'.concat(index)" justify="space-between" dense>
            <v-divider v-if="i.isDivider" class="my-2"/>
            <v-col v-else cols="auto" style="height: 32px;">
              <div style="text-wrap: nowrap; width: 36px; display: inline-block;">
                <v-icon
                  v-for="(icon, index) in i.icon"
                  style="height: 24px;"
                  :key="'icon-'.concat(index)"
                  :small="i.icon.length > 1"
                  color="rgba(0,0,0,0.6)">
                  {{icon}}
                </v-icon>
              </div>
              <div style="display: inline;">
                <span v-if="i.label">{{ $t(i.label) }}:</span>
              </div>
            </v-col>
            <v-col v-if="!i.isDivider" cols="auto">
              {{ $t(i.description) }}
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div id="polygon-editor-top-left-container" :style="topLeftContainerStyle" v-if="showCutInfoContainer">
        <v-row no-gutters>
          <v-col cols="12">
            <v-card :color="SOFT_BLACK">
              <v-card-title class="pt-3 px-3 pb-2">
                <span class="body-2 white--text">{{ $t('tractCut') }}</span>
              </v-card-title>

              <v-card-text class="px-3 pt-0 pb-3">
                <v-row v-for="field in cutInfo" :key="field.id" no-gutters justify="end" align="center" style="display: flex">
                  <v-col cols="">
                    <span class="caption white--text">{{ $t(field.label) }}:</span>
                  </v-col>

                  <v-col cols="auto" class="pl-2">
                    <v-icon v-if="field.label === 'acres' && field.value > MAX_ACRES" color="error" x-small class="mr-1">mdi-alert</v-icon>
                    <span class="caption white--text text-right">{{ field.label === 'acres' ? numberWithCommas(field.value, ACRES_DECIMALS) : field.value }}</span>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div
      :style="toolbarStyle"
      id="toolbar">
        <v-row no-gutters align="center">
          <v-col cols="auto" class="pr-3">
            <div class="map-overlay-toggle-group">
              <Icon
              v-if="showZoomToCut"
              icon="zoom-fit"
              @icon-clicked="zoomToCutClicked"
              xLarge
              :small="false"
              iconColor="white"
              margin="map-option-button"
              tooltipColor="black"
              :tooltipText="$t('centerCamera')"
              elementId="zoom-to-cut"/>
              <Icon
              v-if="overlayState.species.canShow"
              icon="mdi-paw"
              @icon-clicked="toggleSpeciesLayer"
              :disabled="otherOverlayActive('species')"
              :tooltipText="$t('speciesLayer')"
              xLarge
              :small="false"
              :iconColor="overlayState.species.active ? 'primary' : 'grey darken-2'"
              margin="map-option-button"
              tooltipColor="black"
              elementId="polygon-species-layer-toggle"/>
              <Icon
              v-if="showCustomLayers"
              icon="mdi-layers-outline"
              @icon-clicked="toggleCustomLayers"
              :disabled="otherOverlayActive('custom')"
              :tooltipText="$t('customLayers')"
              xLarge
              :small="false"
              :iconColor="overlayState.custom.active || overlayState.custom.open ? 'primary' : 'grey darken-2'"
              margin="map-option-button"
              tooltipColor="black"
              elementId="polygon-custom-layers-toggle"/>
              <Icon
              icon="mdi-crosshairs-gps"
              @icon-clicked="toggleNearbyTracts"
              :tooltipText="$t('toggleNearbyTracts', { toggle: $t(overlayState.nearbyTracts.active ? 'hide' : 'show') })"
              xLarge
              :small="false"
              :iconColor="overlayState.nearbyTracts.active ? 'primary' : 'grey darken-2'"
              margin="map-option-button"
              tooltipColor="black"
              elementId="polygon-nearby-tracts-toggle"/>
              <Icon
              v-if="overlayState.nearbyTracts.active"
              icon="mdi-vector-polygon"
              @icon-clicked="toggleNearbyBoundaries"
              :disabled="otherOverlayActive('nearbyBoundaries')"
              :tooltipText="$t('nearbyBoundaries')"
              xLarge
              :small="false"
              :iconColor="overlayState.nearbyBoundaries.active ? 'primary' : 'grey darken-2'"
              margin="map-option-button"
              tooltipColor="black"
              elementId="polygon-nearby-boundaries-toggle"/>
            </div>
          </v-col>

          <v-col>
            <v-card style="overflow: hidden" color="grey lighten-4">
              <v-row no-gutters>
                <v-col cols="auto" v-for="(button, index) in toolbarButtons" :key="button.id">
                  <v-btn
                  @click="toolbarButtonClicked(button)"
                  :style="toolbarButtonStyle(index)"
                  tile
                  :color="colorForToolbarButton(button)"
                  :class="classForToolbarButton(button)"
                  :ripple="false"
                  elevation="0">
                    <v-row no-gutters>
                      <v-col cols="12">
                        <v-icon :style="toolbarButtonIconStyle">
                          {{ button.icon }}
                        </v-icon>
                      </v-col>
                      <v-col cols="12" :style="toolbarButtonTextStyle">
                        <span>
                          {{ $t(button.label) }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-tooltip
      color="primary"
      left
      v-for="(option, i) in editOptions"
      :key="i"
      :disabled="hintsEnabled">

        <template #activator="{ on }">
          <v-btn
            :ripple="false"
            @click="onEditOptionClick(option.label)"
            v-on="on"
            :style="styleForEditModeOption(i)"
            :fab="!hintsEnabled"
            :small="!hintsEnabled"
            elevation="2"
            :class="classForEditOption(option)"
            :height="hintsEnabled ? 44 : ''"
            :width="hintsEnabled ? 96 : ''"
            :color="currentEditModeOption === option.label ? PALE_HEARTWOOD_ORANGE : 'white'"
            :id="kebabCase(option.label)">
            <v-row no-gutters>
              <v-col cols="12">
                <v-icon>{{ option.icon }}</v-icon>
              </v-col>
              <v-col cols="12" v-if="hintsEnabled">
                <span style="font-size: 11px; letter-spacing: 0.15px;">{{ $t(option.label) }}</span>
              </v-col>
            </v-row>
          </v-btn>
        </template>

        <span class="subtitle-1 white--text">{{ $t(option.label) }}</span>
      </v-tooltip>

      <v-card flat :color="alert?.type?.color" v-if="alert" :style="alertStyle">
        <v-card-text class="white--text pa-3">
          <v-row align="center" dense>
            <v-col cols="auto">
              <v-icon small color="white" class="pa-0 mr-1" style="transform: translate(0px, -2px)">mdi-alert-circle</v-icon>
              <span>{{ alert.message }} </span>
            </v-col>
            <v-col cols="auto" v-if="alert.features">
              <v-btn v-if="includeSimplifyButton" @click="simplifyClicked" small elevation="0" class="warning--text mt-n3 mb-n1" color="white">{{ $t('simplify') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card :style="legend.containerStyle" id="polygon-map-legend" v-if="$vuetify.breakpoint.lgAndUp && !loading">
        <v-col
        v-for="(legendItem) in legendContent"
        :key="legendItem.label"
        :style="{paddingTop: '4px', cursor: legendItem.label === 'parcel' ? 'pointer' : ''}"
        @click="toggleLegendItemVisibility(legendItem)"
        >
          <v-row dense align="center" :id="legendItem.elementId">
            <v-col cols="auto">
              <v-icon
              color="white"
              small
              v-if="legendItem.hidden">
                mdi-eye-off
              </v-icon>
              <div
              v-else
              :style="legendItem.style">
              </div>
            </v-col>
            <v-col cols="auto">
              <span class="caption white--text">{{ $t(legendItem.label) }}</span>
            </v-col>
          </v-row>
        </v-col>
      </v-card>

      <v-card outlined :style="actionsStyle">
        <v-card-text class="pa-2">
          <v-row justify="end" align="center" dense>
            <v-col cols="auto" :style="optionsStyle">
              <v-row dense align="center">
                <v-col cols="auto">
                  <v-btn icon color="grey" class="pa-0 ml-n2 mr-n2 my-n2" @click="chevronClicked">
                    <v-icon>mdi-chevron-double-right</v-icon>
                  </v-btn>
                </v-col>

                <v-col cols="auto">
                  <Icon
                  margin="my-1 mr-1"
                  :icon="marker ? 'mdi-map-marker-off' : 'mdi-map-marker'"
                  :tooltipText="marker ? $t('hideMarker') : $t('showMarker')"
                  @icon-clicked="marker = !marker"
                  :small="false"/>
                </v-col>

                <v-col cols="auto" v-if="$vuetify.breakpoint.lgAndUp">
                  <v-switch
                    inset
                    dense
                    class="py-0 mb-n4 mt-1"
                    v-model="hintsEnabled"
                    @change="hintsChanged"
                    id="toggle-tract-shape-hints">
                    <template #label>
                      <span class="ml-n3 mr-2 body-2">{{ $t('hints') }}</span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="auto">
              <Icon icon="mdi-cog" :tooltipText="$t('options')" @icon-clicked="optionsClicked" :small="false"/>
            </v-col>

            <v-col cols="auto">
              <v-btn
                @click="$emit('shape-cancelled')"
                small
                outlined
                color="primary"
                id="cancel-tract-shape">
                {{$t('cancel')}}
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                :disabled="disableSaveButton"
                elevation="0"
                small
                @click="handleShapeSaved"
                class="primary white--text"
                id="save-tract-shape">
                {{ confirmShapeButtonText }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <LoadableComponent :loading="loading">
        <PolygonMap
        v-if="!loading"
        :tract="tract"
        :containerId="mapContainerId"
        :hintsEnabled="hintsEnabled && $vuetify.breakpoint.lgAndUp"
        :angelGeojson="angelGeojson"
        :propShowMarker="marker"
        :propShowParcel="renderParcelOnMap"
        :currentDrawingMode="currentDrawingMode"
        :currentEditModeOption="currentEditModeOption"
        :zoomToCutTrigger="zoomToCutTrigger"
        :simplifyTrigger="simplifyTrigger"
        :deselectTrigger="deselectTrigger"
        @map-ready="map = $event"
        @drawing-manager-initialized="assignDrawingManager"
        @drawing-mode-changed="onDrawingModeChanged"
        @edit-mode-changed="onEditModeChanged"
        @save-button-disabled="onSaveButtonChanged"
        @alert="onAlert"
        @acres="acresUpdated"/>
      </LoadableComponent>

    </v-card>
  </v-card>
</template>
<script>
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { overlaysStack } from '@/utils/map/overlayLayers.js'
import {
  instructions, getDrawingLegendInstance, DrawingMode,
  MapDrawingAlert, MapDrawingAlertFeature
} from '@/utils/map/MapDrawingManagerHelpers'
import { zoomFit } from '@/images/AngelSvgLibrary'
import { getAcresFromGeojson, ACRES_DECIMALS } from '@/utils/map/GeospatialHelpers'
import { kebabCase } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { MAX_ACRES } from '@/utils/rules'
import { numberWithCommas } from '@/utils/NumericMutations.js'

const SOFT_BLACK = 'rgba(34, 34, 34, 0.867)'
const PALE_HEARTWOOD_ORANGE = '#faefe9'
const STANDARD_PADDING = 8
const INSTRUCTION_HEIGHT = 32
const DIVIDER_HEIGHT = 17
const INSTRUCTION_HEIGHT_OFFSET = 24
const INSTRUCTION_HEIGHT_DEFAULT = 48
const INSTRUCTION_WIDTH = 300
const px = (value) => `${value}px`
const PARCELS = AngelGeojsonLayer.Parcels
const CUT = AngelGeojsonLayer.Cut

export default {
  name: 'PolygonEditor',
  components: {
    PolygonMap: () => import('@/components/tract/tract-form/PolygonMap.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    MapOverlays: () => import('@/components/maps/MapOverlays.vue')
  },

  props: {
    angelGeojson: {
      type: AngelGeojson,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    tract: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    MAX_ACRES,
    SOFT_BLACK,
    PALE_HEARTWOOD_ORANGE,
    ACRES_DECIMALS,
    acres: 0,
    zoomFit,
    loading: true,
    mapContainerId: 'polygon-map-container',
    drawingManager: undefined,
    hintsEnabled: localStorage.getItem(LocalStorageKeys.TRACT_SHAPE_EDIT_SHOW_HINTS) !== 'false',
    currentDrawingMode: DrawingMode.Idle,
    currentEditModeOption: '',
    viewOptions: false,
    marker: true,
    saveDisabled: true,
    alert: MapDrawingAlert.None,
    zoomToCutTrigger: false,
    simplifyTrigger: false,
    deselectTrigger: false,
    legend: null,
    renderParcelOnMap: true,
    map: null,
    overlayState: { species: { canShow: false, active: false }, topo: { active: false }, soil: { active: false }, custom: { active: false, open: false }, nearbyTracts: { active: false }, nearbyBoundaries: { active: false } }
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),

    showCustomLayers () {
      return this.companyInfo.hasCustomMapLayers
    },

    toolActive () {
      return this.currentDrawingMode.id !== DrawingMode.Idle.id
    },

    hasShape () {
      return this.drawingManager?.getSource()?.getShapes().length > 0
    },

    parcels () {
      return this.angelGeojson?.getLayer(PARCELS)
    },

    hasParcels () {
      return this.angelGeojson?.layerIsNotEmpty(PARCELS)
    },

    toolbarButtons () {
      return Object.values(DrawingMode).filter(drawingMode => {
        const preventNewShapes = this.alert?.preventNewShapes
        const followNewShapePrevention = !['draw-polygon', 'draw-rectangle'].includes(drawingMode.azMapsLabel) || !preventNewShapes
        const preventEditShapes = this.alert?.preventEditShapes
        const followEditShapePrevention = !['edit-geometry'].includes(drawingMode.azMapsLabel) || !preventEditShapes
        const followNoEditOrEraseWhenNoShapes = !['edit-geometry', 'erase-geometry'].includes(drawingMode.azMapsLabel) || this.hasShape
        return followNoEditOrEraseWhenNoShapes && followNewShapePrevention && followEditShapePrevention
      })
    },

    parcelInfo () {
      const parcelFieldsForDisplay = [{
        label: 'parcelNumber',
        parcelKey: 'parcel_id'
      }, {
        label: 'owner',
        parcelKey: 'owner'
      }]

      const parcelFeature = this.angelGeojson?.getFeatures(PARCELS)[0]
      if (!parcelFeature) return undefined
      const props = parcelFeature?.properties?.fields
      const result = parcelFieldsForDisplay
        .filter(field => props[field.parcelKey] ?? false)
        .map(field => ({
          ...field,
          value: props[field.parcelKey]
        }))
      if (result.length < 1) {
        return [{
          label: 'parcelNumber',
          value: this.$t('unknown')
        }]
      }
      return result
    },

    cutInfo () {
      return [{
        label: 'acres',
        value: this.acres
      }]
    },

    instructionsClass () {
      return this.hintsEnabled && this.$vuetify.breakpoint.lgAndUp && this.instructions ? 'visible in-front' : 'invisible behind'
    },

    containerClass () {
      return this.hintsEnabled && this.$vuetify.breakpoint.lgAndUp ? 'hints-enabled' : ''
    },

    instructions () {
      return instructions(this.currentDrawingMode, this.currentEditModeOption, this.drawingManager?.editHelper?.shape != null, this.drawingManager?.source?.shapes?.length > 1)
    },

    legendContent () {
      return this.legend?.content
    },

    shapeIsSelected () {
      return !!this.drawingManager?.editHelper?.shape
    },

    editOptions () {
      return [{
        label: 'dragVertices',
        icon: 'mdi-vector-point-select'
      }, {
        label: 'eraseVertices',
        icon: 'mdi-vector-square-remove'
      }, {
        label: 'dragAndRotate',
        icon: 'mdi-cursor-move'
      },
      this.hasParcels ? {
        label: 'matchParcel',
        isAction: true,
        icon: 'mdi-vector-link',
        onClick: () => { this.onEditOptionClick('matchParcel', false) }
      } : undefined
      ].filter(i => i)
    },

    showZoomToCut () {
      return this.drawingManager?.getSource()?.getShapes() || this.angelGeojson.layerIsNotEmpty('parcels')
    },

    confirmShapeButtonText () {
      return this.$t('confirm')
    },

    instructionsStyle () {
      const height = this.instructions
        ? px((this.instructions?.filter(i => !i.isDivider).length * INSTRUCTION_HEIGHT) + (this.instructions?.filter(i => i.isDivider === true).length * DIVIDER_HEIGHT) + INSTRUCTION_HEIGHT_OFFSET)
        : px(INSTRUCTION_HEIGHT_DEFAULT)
      const width = px(INSTRUCTION_WIDTH)
      return { position: 'absolute', bottom: '52px', left: px(STANDARD_PADDING), border: 'none', width, height, transition: '.1s' }
    },

    topLeftContainerStyle () {
      const showTopLeftContainer = this.viewingParcelInfo || this.showCutInfoContainer
      const opacity = showTopLeftContainer ? '1' : '0'
      const zIndex = showTopLeftContainer ? 1 : -1
      return { position: 'absolute', top: px(STANDARD_PADDING), left: px(STANDARD_PADDING), zIndex, transition: '.1s', overflow: 'hidden', maxWidth: 'max(200px, 20%)', opacity }
    },

    showCutInfoContainer () {
      return this.acres > 0 && !this.alert?.hideAcreCount && this.$vuetify.breakpoint.lgAndUp
    },

    toolbarStyle () { return { position: 'absolute', zIndex: 1, right: px(STANDARD_PADDING), top: px(STANDARD_PADDING) } },

    toolbarButtonIconStyle () { return { fontSize: '1.5rem' } },

    toolbarButtonTextStyle () {
      const opacity = this.hintsEnabled ? 1 : 0
      const height = this.hintsEnabled ? 'auto' : 0
      return { paddingTop: '1px', opacity, height, transition: 'inherit' }
    },

    actionsStyle () {
      const width = `${this.viewOptions && this.$vuetify.breakpoint.lgAndUp
      ? 380
      : this.viewOptions
        ? 308
        : 226}px`
      return { position: 'absolute', bottom: px(STANDARD_PADDING), right: px(STANDARD_PADDING), zIndex: 1, border: 'none', width: width, transition: '.1s', overflow: 'hidden', height: '44px' }
    },

    alertStyle () { return { position: 'absolute', bottom: '60px', right: px(STANDARD_PADDING), zIndex: 1, border: 'none', transition: '.1s', overflow: 'hidden', height: '44px' } },

    optionsStyle () {
      const width = this.viewOptions ? '100%' : '0px'
      const opacity = this.viewOptions ? '1' : '0'
      const zIndex = this.viewOptions ? 0 : -1
      return { position: 'absolute', left: '0', bottom: '0', overflow: 'hidden', transition: '.1s', height: '42px', width, opacity, zIndex }
    },

    disableSaveButton () {
      return this.saveDisabled || this.acres > MAX_ACRES
    },

    includeSimplifyButton () {
      return this.alert?.features.some(f => f.id === MapDrawingAlertFeature.SimplifyButton.id)
    }
  },

  async created () {
    this.legend = getDrawingLegendInstance()
    this.legend.viewingCut = true
    this.acres = getAcresFromGeojson(this.angelGeojson.getLayer(CUT))
    await this.initializeParcel()
    this.loading = false
  },

  mounted () {
    document.addEventListener('keydown', this.keyListener)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.keyListener)
  },

  watch: {
    'drawingManager.editHelper.shape': {
      handler (shape) {
        this.legend.hasFocusedShape = !!shape
      }
    },

    angelGeojson: {
      handler (angelGeojson) {
        this.legend.viewingParcel = angelGeojson.layerIsNotEmpty(PARCELS)
      },
      deep: true
    }
  },

  methods: {
    numberWithCommas,
    kebabCase,

    ...mapActions('tract', ['getParcelForCoordinate']),

    async handleShapeSaved () {
      var drawingSource = this.drawingManager.getSource()
      var drawingSourceJson = drawingSource.toJson()
      this.removeEditorProperties(drawingSourceJson)
      const cut = drawingSourceJson
      this.$emit('shape-saved', { cut, acres: getAcresFromGeojson(cut) })
    },

    async initializeParcel () {
      this.legend.viewingParcel = this.angelGeojson.layerIsNotEmpty(PARCELS)
    },

    removeEditorProperties (featureCollection) {
      featureCollection.features.forEach(feature => {
        const props = feature.properties
        delete props.status
        delete props.strokeColor
        delete props.fillColor
      })
    },

    assignDrawingManager (drawingManager) {
      this.drawingManager = drawingManager
    },

    toggleSpeciesLayer () {
      if (this.otherOverlayActive('species')) {
        this.setSnack(this.$t('disableOtherLayersToToggle', { layer: this.$t('speciesLayer') }))
        return
      }
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleSpeciesLayer()
    },

    toggleCustomLayers () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleCustomLayersPanel()
    },

    toggleNearbyTracts () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyTracts(this.tract)
    },

    toggleNearbyBoundaries () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleNearbyTractBoundaries(this.tract)
    },

    otherOverlayActive (name) {
      return ['species', 'custom', 'nearbyBoundaries'].some(o => o !== name && !overlaysStack(name, o) && this.overlayState[o]?.active)
    },

    onDrawingModeChanged (mode) {
      this.currentDrawingMode = mode
      this.legend.currentDrawingMode = mode
      if (mode === DrawingMode.EditGeometry) {
        this.currentEditModeOption = 'dragVertices'
      }
    },

    onEditModeChanged (option) {
      this.currentEditModeOption = option
    },

    onSaveButtonChanged (val) {
      this.saveDisabled = val
    },

    optionsClicked () {
      this.viewOptions = !this.viewOptions
    },

    chevronClicked () {
      this.viewOptions = false
    },

    onAlert (alert) {
      this.alert = alert
      if (!alert) {
        if (this.acres > MAX_ACRES) {
          this.alert = MapDrawingAlert.TooBig
        }
      }
      this.legend.hasInvalidShape = alert?.showInvalidInLegend
      this.handleModeOnAlert()
    },

    handleModeOnAlert () {
      if (!this.toolbarButtons.some(tb => tb.id === this.currentDrawingMode.id)) {
        this.setToIdle()
      }
    },

    zoomToCutClicked () {
      this.zoomToCutTrigger = true
      this.$nextTick(_ => { this.zoomToCutTrigger = false })
    },

    simplifyClicked () {
      this.simplifyTrigger = true
      this.$nextTick(_ => { this.simplifyTrigger = false })
    },

    deselectPressed () {
      this.deselectTrigger = true
      this.$nextTick(_ => { this.deselectTrigger = false })
    },

    toolbarButtonClicked (button) {
      this.onDrawingModeChanged(button)
    },

    toggleLegendItemVisibility (legendItem) {
      if (legendItem.label === 'parcel') {
        legendItem.hidden = !legendItem.hidden
        this.renderParcelOnMap = !legendItem.hidden
      }
    },

    parcelInfoToggled () {
      this.viewingParcelInfo = !this.viewingParcelInfo
    },

    onEditOptionClick (option) {
      this.currentEditModeOption = option
    },

    classForEditOption ({ label, isAction }) {
      if (isAction) return 'primary white--text edit-option-button'
      return `${this.currentEditModeOption === label ? 'primary--text active' : 'grey--text text--darken-2'} edit-option-button`
    },

    styleForEditModeOption (index) {
      const zIndex = this.currentDrawingMode.azMapsLabel === 'edit-geometry' ? 1 : -1
      const opacity = this.currentDrawingMode.azMapsLabel === 'edit-geometry' ? 1 : 0
      const transition = `z-index ${this.currentDrawingMode.azMapsLabel === 'edit-geometry' ? 0 : 1}s, opacity .25s`
      const buffer = 64
      const height = this.hintsEnabled ? 52 : 48
      const top = `${buffer + (height * index)}px`
      return { position: 'absolute', top, right: '8px', zIndex, opacity, transition }
    },

    hintsChanged (showHints) {
      localStorage.setItem(LocalStorageKeys.TRACT_SHAPE_EDIT_SHOW_HINTS, showHints)
    },

    classForToolbarButton (button) {
      return this.currentDrawingMode.azMapsLabel === button.azMapsLabel ? 'primary--text' : 'grey--text text--darken-2'
    },

    colorForToolbarButton (button) {
      return this.currentDrawingMode.azMapsLabel === button.azMapsLabel ? PALE_HEARTWOOD_ORANGE : 'white'
    },

    toolbarButtonStyle (index) {
      const includeBorder = index < this.toolbarButtons.length - 1
      const width = px(this.hintsEnabled ? 128 : 48)
      const height = px(48)
      const fontSize = px(12)
      const lineHeight = px(18)
      const marginRight = includeBorder ? '1px' : '0px'
      return { width, height, fontSize, borderRadius: 0, lineHeight, transition: '.1s', letterSpacing: px(0.4), marginRight, overflow: 'hidden' }
    },

    keyListener (event) {
      switch (event.keyCode) {
        case 27: { // esc
          if (this.currentDrawingMode.azMapsLabel !== 'idle') {
            this.setToIdle()
          } else {
            const cancelBtn = document.getElementById('cancel-tract-shape')
            cancelBtn.focus()
          }
          break
        }
        case 13: { // enter
          if (this.currentDrawingMode.azMapsLabel === 'edit-geometry') this.deselectPressed()
        }
      }
    },

    setToIdle () {
      this.onDrawingModeChanged(DrawingMode.Idle)
    },

    acresUpdated (acres) {
      this.acres = parseFloat(acres)
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
